/* eslint-disable arrow-body-style */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import {
  ShareBoostConfig,
  ShareBoostConfigRequest,
  DEFAULT_SHAREBOOST_CONFIG,
  ShareBoostAnalytics,
  CashbackTransaction,
  UserReferralStats,
  TopSharer,
  ShareClick,
} from 'src/@types/shareboost';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

export const shareBoostApi = createApi({
  reducerPath: 'shareBoostApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['ShareBoostConfig', 'ShareBoostAnalytics'],
  endpoints: (builder) => ({
    // Get ShareBoost configuration for an event
    getShareBoostConfig: builder.query<ShareBoostConfig, string>({
      queryFn: async (eventId) => {
        try {
          const configRef = doc(DB, 'events', eventId, 'shareBoostConfig', 'config');
          const configDoc = await getDoc(configRef);

          if (configDoc.exists()) {
            const data = configDoc.data() as ShareBoostConfig;
            return { data };
          }

          // Return default config if none exists
          return { data: DEFAULT_SHAREBOOST_CONFIG };
        } catch (error: any) {
          console.error('Error fetching ShareBoost config:', error);
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, eventId) => [{ type: 'ShareBoostConfig', id: eventId }],
    }),

    // Update ShareBoost configuration
    updateShareBoostConfig: builder.mutation<ShareBoostConfig, ShareBoostConfigRequest>({
      queryFn: async ({ eventId, config }) => {
        try {
          console.log('Updating ShareBoost config for event:', eventId, config);
          const configRef = doc(DB, 'events', eventId, 'shareBoostConfig', 'config');
          const configDoc = await getDoc(configRef);

          if (configDoc.exists()) {
            // Update existing config
            await updateDoc(configRef, {
              ...config,
              updatedAt: serverTimestamp(),
            } as any);
          } else {
            // Create new config with defaults
            await setDoc(configRef, {
              ...DEFAULT_SHAREBOOST_CONFIG,
              ...config,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          }

          // Fetch and return the updated config
          const updatedDoc = await getDoc(configRef);
          const data = updatedDoc.data() as ShareBoostConfig;

          return { data };
        } catch (error: any) {
          console.error('Error updating ShareBoost config:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: 'ShareBoostConfig', id: eventId }],
    }),

    // Enable/Disable ShareBoost
    toggleShareBoost: builder.mutation<ShareBoostConfig, { eventId: string; enabled: boolean }>({
      queryFn: async ({ eventId, enabled }) => {
        try {
          const configRef = doc(DB, 'events', eventId, 'shareBoostConfig', 'config');
          const configDoc = await getDoc(configRef);

          if (configDoc.exists()) {
            await updateDoc(configRef, {
              enabled,
              updatedAt: serverTimestamp(),
            });
          } else {
            // Create new config with enabled status
            const newConfig = {
              ...DEFAULT_SHAREBOOST_CONFIG,
              enabled,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            await setDoc(configRef, newConfig);
          }

          // Fetch and return the updated config
          const updatedDoc = await getDoc(configRef);
          const data = updatedDoc.data() as ShareBoostConfig;

          return { data };
        } catch (error: any) {
          console.error('Error toggling ShareBoost:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: 'ShareBoostConfig', id: eventId }],
    }),

    // Update ShareBoost statistics (called by backend when conversions happen)
    updateShareBoostStats: builder.mutation<
      ShareBoostConfig,
      { eventId: string; stats: Partial<Pick<ShareBoostConfig, 'totalCashbackPaid' | 'totalConversions' | 'totalShares'>> }
    >({
      queryFn: async ({ eventId, stats }) => {
        try {
          const configRef = doc(DB, 'events', eventId, 'shareBoostConfig', 'config');

          await updateDoc(configRef, {
            ...stats,
            updatedAt: serverTimestamp(),
          });

          // Fetch and return the updated config
          const updatedDoc = await getDoc(configRef);
          const data = updatedDoc.data() as ShareBoostConfig;

          return { data };
        } catch (error: any) {
          console.error('Error updating ShareBoost stats:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: 'ShareBoostConfig', id: eventId }],
    }),

    // Get ShareBoost Analytics for an event
    getShareBoostAnalytics: builder.query<ShareBoostAnalytics, string>({
      queryFn: async (eventId) => {
        try {
          // Fetch ShareBoost config for budget info
          const configRef = doc(DB, 'events', eventId, 'shareBoostConfig', 'config');
          const configDoc = await getDoc(configRef);
          const config = configDoc.exists() ? (configDoc.data() as ShareBoostConfig) : DEFAULT_SHAREBOOST_CONFIG;

          // Fetch all cashback transactions for this event
          const transactionsRef = collection(DB, 'cashbackTransactions');
          const transactionsQuery = query(
            transactionsRef,
            where('eventId', '==', eventId),
            orderBy('createdAt', 'desc'),
            limit(100)
          );
          const transactionsSnapshot = await getDocs(transactionsQuery);
          const transactions: CashbackTransaction[] = transactionsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as CashbackTransaction[];

          // Fetch user referral stats using collectionGroup
          const referralStatsQuery = query(
            collection(DB, 'users'),
          );
          // Note: We'll filter client-side for this event since collectionGroup with where
          // on nested collection is complex

          // Calculate analytics
          const totalSharers = new Set(transactions.map(t => t.sharerId)).size;
          const totalConversions = transactions.filter(t =>
            ['pending', 'processing', 'completed'].includes(t.status)
          ).length;
          const totalClicks = transactions.filter(t => t.status === 'clicked').length + totalConversions;

          // Calculate total shares as the number of unique referral codes that have been used
          // This is more accurate than relying on config.totalShares which isn't being tracked
          const totalShares = new Set(transactions.map(t => t.referralCode)).size;

          const pendingTransactions = transactions.filter(t => t.status === 'pending');
          const processingTransactions = transactions.filter(t => t.status === 'processing');
          const completedTransactions = transactions.filter(t => t.status === 'completed');
          const cancelledTransactions = transactions.filter(t => ['cancelled', 'failed'].includes(t.status));

          const totalRevenue = transactions
            .filter(t => ['pending', 'processing', 'completed'].includes(t.status))
            .reduce((sum, t) => sum + t.ticketAmount, 0);

          const totalCashbackAllocated = transactions
            .filter(t => ['pending', 'processing', 'completed'].includes(t.status))
            .reduce((sum, t) => sum + t.cashbackAmount, 0);

          const totalCashbackPaid = completedTransactions.reduce((sum, t) => sum + t.cashbackAmount, 0);

          // Calculate top sharers
          const sharerMap = new Map<string, {
            sharerId: string;
            sharerName: string;
            sharerEmail: string;
            totalReferrals: number;
            totalRevenue: number;
            totalCashbackEarned: number;
            totalClicks: number;
          }>();

          transactions.forEach(t => {
            if (!sharerMap.has(t.sharerId)) {
              sharerMap.set(t.sharerId, {
                sharerId: t.sharerId,
                sharerName: t.sharerName || t.sharerEmail || 'Unknown',
                sharerEmail: t.sharerEmail,
                totalReferrals: 0,
                totalRevenue: 0,
                totalCashbackEarned: 0,
                totalClicks: 0,
              });
            }

            const sharer = sharerMap.get(t.sharerId)!;

            if (t.status === 'clicked') {
              sharer.totalClicks += 1;
            } else if (['pending', 'processing', 'completed'].includes(t.status)) {
              sharer.totalReferrals += 1;
              sharer.totalRevenue += t.ticketAmount;
              sharer.totalCashbackEarned += t.cashbackAmount;
              sharer.totalClicks += 1; // Conversion also counts as click
            }
          });

          // Fetch user data for sharers with missing names
          const sharerIds = Array.from(sharerMap.keys());
          const userDataPromises = sharerIds
            .filter(sharerId => {
              const sharer = sharerMap.get(sharerId)!;
              return !sharer.sharerName || sharer.sharerName === sharer.sharerEmail || sharer.sharerName === 'Unknown';
            })
            .map(async (sharerId) => {
              try {
                const userRef = doc(DB, 'users', sharerId);
                const userDoc = await getDoc(userRef);
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    sharerId,
                    displayName: userData.displayName || userData.firstName || userData.email || 'Unknown',
                  };
                }
              } catch (error) {
                console.error(`Error fetching user ${sharerId}:`, error);
              }
              return null;
            });

          const userDataResults = await Promise.all(userDataPromises);

          // Update sharer names with fetched user data
          userDataResults.forEach(result => {
            if (result) {
              const sharer = sharerMap.get(result.sharerId);
              if (sharer) {
                sharer.sharerName = result.displayName;
              }
            }
          });

          const topSharers: TopSharer[] = Array.from(sharerMap.values())
            .sort((a, b) => b.totalCashbackEarned - a.totalCashbackEarned)
            .slice(0, 10)
            .map(s => ({
              ...s,
              conversionRate: s.totalClicks > 0 ? (s.totalReferrals / s.totalClicks) * 100 : 0,
              status: 'active',
            }));

          // Recent transactions (last 20)
          const recentTransactions = transactions.slice(0, 20);

          // Recent clicks (last 20 click-only transactions)
          const recentClickTransactions = transactions
            .filter(t => t.status === 'clicked')
            .slice(0, 20);

          const recentClicks: ShareClick[] = recentClickTransactions.map(t => ({
            id: t.id,
            sharerId: t.sharerId,
            sharerName: t.sharerName,
            referralCode: t.referralCode,
            eventId: t.eventId,
            referredUserId: t.referredUserId,
            referredUserEmail: t.referredUserEmail,
            potentialAmount: t.ticketAmount,
            clickedAt: t.createdAt,
          }));

          const analytics: ShareBoostAnalytics = {
            eventId,
            totalSharers,
            totalShares,
            totalClicks,
            clickThroughRate: totalShares > 0 ? (totalClicks / totalShares) * 100 : 0,
            totalConversions,
            conversionRate: totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0,
            totalRevenue,
            totalCashbackAllocated,
            totalCashbackPaid,
            budgetRemaining: config.maxTotalBudget - totalCashbackAllocated,
            pendingCount: pendingTransactions.length,
            pendingAmount: pendingTransactions.reduce((sum, t) => sum + t.cashbackAmount, 0),
            processingCount: processingTransactions.length,
            processingAmount: processingTransactions.reduce((sum, t) => sum + t.cashbackAmount, 0),
            completedCount: completedTransactions.length,
            completedAmount: totalCashbackPaid,
            cancelledCount: cancelledTransactions.length,
            cancelledAmount: cancelledTransactions.reduce((sum, t) => sum + t.cashbackAmount, 0),
            topSharers,
            recentTransactions,
            recentClicks,
          };

          return { data: analytics };
        } catch (error: any) {
          console.error('Error fetching ShareBoost analytics:', error);
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, eventId) => [{ type: 'ShareBoostAnalytics', id: eventId }],
    }),
  }),
});

export const {
  useGetShareBoostConfigQuery,
  useUpdateShareBoostConfigMutation,
  useToggleShareBoostMutation,
  useUpdateShareBoostStatsMutation,
  useGetShareBoostAnalyticsQuery,
} = shareBoostApi;
